import React, { useEffect, useState, useRef } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getAuthToken, isActingAs, getActingAs, setActingAs } from './authUtils';
import authEvents from './authEvents';
import { store } from '../store';
import { login, updateUser } from '../store/authSlice';
import axiosInstance from '../axiosInstance';
import { toast } from '../components/toast';

const AuthWrapper = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const hadAuthRef = useRef(false);
  const verificationCheckedRef = useRef(false);
  const validatedRef = useRef(false);

  // Track if user ever had valid auth in this session
  useEffect(() => {
    if (user && token) {
      hadAuthRef.current = true;
    }
  }, [user, token]);

  // Main validation — runs once on mount (PersistGate guarantees Redux is rehydrated)
  useEffect(() => {
    if (validatedRef.current) return;

    const validateToken = async () => {
      const storageToken = getAuthToken();

      // Check if we're in the middle of an impersonation swap
      const impersonationPending = localStorage.getItem('impersonation_pending');
      if (impersonationPending && storageToken) {
        // IMPORTANT: do NOT remove the impersonation_pending flag until
        // AFTER /user resolves. The previous code removed it before the
        // await, so if the user refreshed mid-flow (or the AuthWrapper
        // re-mounted), the flag was already gone — the impersonation
        // branch was skipped, Redux was empty, storage had the bearer
        // token, and the (!user || !token || !storageToken) check
        // triggered the "Session expired" modal instead of completing
        // the swap. Keeping the flag alive until success means refreshes
        // simply retry the same flow.
        try {
          const res = await axiosInstance.get('/user', {
            headers: { Authorization: `Bearer ${storageToken}` },
          });
          dispatch(login({ user: res.data, token: storageToken }));
          localStorage.removeItem('impersonation_pending');

          // Surface a confirmation toast on the freshly-booted session.
          // sessionStorage survives the hard reload from impersonation.js
          // but is tab-scoped, so it can't leak to other tabs.
          try {
            const welcomeUser = sessionStorage.getItem('impersonation_welcome_user');
            if (welcomeUser) {
              sessionStorage.removeItem('impersonation_welcome_user');
              toast.success(`Logged in as @${welcomeUser}`);
            }
          } catch {}

          validatedRef.current = true;
          setIsValid(true);
          setIsValidating(false);
          return;
        } catch {
          // The new token was rejected — wipe the slate and let the
          // user log in normally. Clearing localStorage drops the
          // impersonation_pending flag too.
          try { localStorage.clear(); } catch {}
          try { sessionStorage.clear(); } catch {}
          validatedRef.current = true;
          setIsValidating(false);
          setIsValid(false);
          return;
        }
      }

      // Read user/token from Redux (already rehydrated by PersistGate)
      const authState = store.getState().auth;
      const currentUser = authState.user;
      const currentToken = authState.token;

      if (!currentUser || !currentToken || !storageToken) {
        // No interstitial, no modal — just send them to /login with the
        // current URL stashed in location.state so useLoginForm /
        // useRegisterForm / useForgotPasswordForm can navigate the user
        // back to where they were after a successful (re-)auth.
        validatedRef.current = true;
        setIsValid(false);
        setIsValidating(false);
        return;
      }

      // Auth data present — let the user through immediately
      validatedRef.current = true;
      setIsValid(true);
      setIsValidating(false);

      // Check email verification in background (non-blocking)
      if (!verificationCheckedRef.current) {
        verificationCheckedRef.current = true;
        try {
          const res = await axiosInstance.get('/email/verification-status');
          if (res.data.success && res.data.data.verification_required && !res.data.data.email_verified) {
            setNeedsEmailVerification(true);
          }
        } catch {
          // If check fails, don't block
        }
      }
    };

    validateToken();
  }, [dispatch]);

  // Listen for session-expired events from axiosInstance's 401 handler.
  // We no longer show a modal — just bounce the wrapper to "not valid"
  // so the existing <Navigate to="/login" state={{from}}/> below fires
  // and the login form takes them back to where they were.
  useEffect(() => {
    const handleSessionExpired = () => {
      setIsValid(false);
    };
    const unsubExpired = authEvents.on('sessionExpired', handleSessionExpired);
    return () => unsubExpired();
  }, []);

  // Background refresh: re-fetch user profile to get fresh presigned avatar URLs
  // Runs on initial load and when tab regains focus (handles expired URLs after idle)
  const profileRefreshedRef = useRef(false);
  const lastRefreshRef = useRef(0);
  useEffect(() => {
    if (!isValid || !token) return;

    const refreshProfile = async () => {
      const now = Date.now();
      // Refresh interval: 1 min for team members (to pick up role changes), 5 min otherwise
      const interval = isActingAs() ? 60 * 1000 : 5 * 60 * 1000;
      if (now - lastRefreshRef.current < interval) return;
      lastRefreshRef.current = now;
      try {
        const res = await axiosInstance.get('/user');
        if (res.data) {
          dispatch(updateUser(res.data));

          // If acting as a creator, sync fresh permissions from backend
          // so role updates apply immediately without re-switching
          if (isActingAs() && res.data._permissions) {
            const actingAs = getActingAs();
            if (actingAs) {
              setActingAs({
                ...actingAs,
                permissions: res.data._permissions,
                role: res.data._role || actingAs.role,
              });
            }
          }
        }
      } catch {
        // Non-blocking — if refresh fails, user still sees the app with stale data
      }
    };

    // Initial refresh on mount
    if (!profileRefreshedRef.current) {
      profileRefreshedRef.current = true;
      refreshProfile();
    }

    // Refresh when tab becomes visible again (user returns after being away)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshProfile();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isValid, token, dispatch]);

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Validating authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated. location.state.from is read
  // by useLoginForm / useRegisterForm / useForgotPasswordForm and the
  // user gets sent back there after a successful auth.
  if (!isValid) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to email verification if required and not verified
  if (needsEmailVerification) {
    return <Navigate to="/verify-email" replace />;
  }

  // User is authenticated
  return <Outlet />;
};

export default AuthWrapper;
